import { Feature } from 'ol';
import * as ol from 'ol';
import GeoJSON from 'ol/format/GeoJSON';
import * as turf from '@turf/turf';
import OverlayOp from 'jsts/org/locationtech/jts/operation/overlay/OverlayOp';
import GeometryFactory  from 'jsts/org/locationtech/jts/geom/GeometryFactory';
import RelateOp from 'jsts/org/locationtech/jts/operation/relate/RelateOp';
import IsValidOp from 'jsts/org/locationtech/jts/operation/valid/IsValidOp'
import BufferOp from 'jsts/org/locationtech/jts/operation/buffer/BufferOp'
import UnionOp from 'jsts/org/locationtech/jts/operation/union/UnionOp';
import GeoJSONReader from 'jsts/org/locationtech/jts/io/GeoJSONReader';
import OL3Parser from 'jsts/org/locationtech/jts/io/OL3Parser'

import GeoJSONWriter from 'jsts/org/locationtech/jts/io/GeoJSONWriter';

import { Geometry, GeometryCollection, LinearRing, LineString, MultiLineString, MultiPoint, MultiPolygon, Point, Polygon } from 'ol/geom';
import { UsingJoinColumnIsNotAllowedError } from 'typeorm';
import EsriJSON from 'ol/format/EsriJSON';
import { closestOnCircle, Coordinate } from 'ol/coordinate';
import { JsonpClientBackend } from '@angular/common/http';
import { getArea } from 'ol/sphere';
import WKT from 'ol/format/WKT';

interface BufferOptions {
  units?: any;
  amount?: number;
  projection?: string;
}

/**
 * Given an array of features, this function will return a single feature that is the union of all the
 * features in the array
 * @param featureArray - Array<Feature<any>>
 * @returns The union of all the features in the feature array.
 */

export function Union(featureArray: Array<Feature<any>>): Feature<any> {
  if (featureArray.length === 0) {
    return null;
  }

  if (featureArray.length < 2) {
    return featureArray.pop();
  }

  const format = new GeoJSON();
  const convertedFeatures = [];
  let union;

  featureArray.forEach((feature) => {
    let convertedFeature = format.writeFeatureObject(feature);
    convertedFeatures.push(convertedFeature);
  });

  while (convertedFeatures.length != 0) {
    if (union != null) {
      let feature = convertedFeatures.pop();

      union = turf.union(union, feature);
    } else {
      let featureOne = convertedFeatures.pop();
      let featureTwo = convertedFeatures.pop();

      union = turf.union(featureOne, featureTwo);
    }
  }
  return format.readFeature(union);
}

/**
 * It takes two features and returns the difference between them.
 * @param featureOne - The first feature to be used in the difference.
 * @param featureTwo - The feature to subtract from featureOne.
 * @returns The difference of the two polygons.
 */
export function UnionJSTS(
features:Feature<any>[],
  options?
) {
  let projection =
    options?.projection == null ? 'EPSG:28355' : options.projection;


    if (features.length === 0) {
      return null;
    }

    if (features.length < 2) {
      return features.pop();
    }


    const geometryFactory = new GeometryFactory();
    const parser = new OL3Parser(geometryFactory, ol);


    parser.inject(
      Point,
      LineString,
      LinearRing,
      Polygon,
      MultiPoint,
      MultiLineString,
      MultiPolygon,
      GeometryCollection
    );


    const jstsFeatures = features.map(feature => parser.read(feature.getGeometry())).map(geom => {

      if(!isValidGeometry(geom))
      {
          return bufferGeometry(geom, 0);
      }

      return geom;
    });


    let unionFeature;


 while (jstsFeatures.length != 0) {
    if (unionFeature != null) {
      let feature = jstsFeatures.pop();

      unionFeature = UnionOp.union(unionFeature, feature);
    } else {
      let featureOne = jstsFeatures.pop();
      let featureTwo = jstsFeatures.pop();

      unionFeature = UnionOp.union(featureOne, featureTwo);
    }
  }





if(unionFeature != null)
  return new Feature(parser.write(unionFeature));

return null;
}

export const isValidGeometry = (geom: Geometry): boolean => {
  const isValidOp = new IsValidOp(geom);
  return isValidOp.isValid();
};


export const bufferGeometry = (geom: Geometry, amount: number): Geometry => {
  const bufferOp = new BufferOp(geom);
  return bufferOp.getResultGeometry(amount);
};

/**
 * Convert a Feature to GeoJson
 * @param feature - The feature to convert to GeoJSON.
 * @returns The GeoJSON representation of the feature.
 */
export function ConvertFeatureToGeoJson(feature: Feature<any>): any {
  const format = new GeoJSON();

  return format.writeFeature(feature);
}

/**
 * Convert a feature to WKT.
 * @param feature - The feature to convert to WKT
 * @returns A string of the feature in WKT format.
 */
export function ConvertFeatureToWKT(feature: Feature<any>)
{
  const format = new WKT();

  return format.writeFeature(feature);
}

/**
 * ConvertWktToFeature(wkt: string): Feature<any>
 *
 * The function takes a string as input and returns a Feature<any> as output
 * @param {string} wkt - The Well-Known Text string to convert to a feature.
 * @returns A feature object.
 */
export function ConvertWktToFeature(wkt: string): Feature<any>
{
  const format = new WKT();

  return format.readFeature(wkt);
}

/**
 * Convert a GeoJSON FeatureCollection to a Well Known Text (WKT) string
 * @param features - Array<Feature<any>>
 * @returns A string of WKT.
 */
export function ConvertFeatureCollectionToWkt(features: Array<Feature<any>>)
{
  const format = new WKT();

  return format.writeFeatures(features);
}

/**
 * Convert a Well Known Text string to a GeoJSON FeatureCollection
 * @param {string} wkt - string - The WKT string to convert to a feature collection.
 * @returns An array of features.
 */
export function ConvertWktToFeatureCollection(wkt: string):Array<Feature<any>>
{
  const format = new WKT();

  return format.readFeatures(wkt);
}

/**
 * Convert a FeatureCollection to GeoJSON
 * @param features - The array of features to be converted to GeoJSON.
 * @returns The GeoJSON representation of the features.
 */
export function ConvertFeatureCollectionToGeoJson(
  features: Array<Feature<any>>
): string {
  const format = new GeoJSON();

  return format.writeFeatures(features);
}

/**
 * Convert GeoJson to a Feature
 * @param {string} geojsonString - The GeoJSON string to convert to a feature.
 * @returns The GeoJSON is converted to a Feature.
 */
export function ConvertGeoJsonToFeature(
  geojsonString: string,
  projection?: string
) {
  const format = new GeoJSON();

  return format.readFeature(geojsonString, { featureProjection: projection });
}

/**
 * Convert a GeoJSON string to a GeoJSON feature collection
 * @param {string} geojsonString - The GeoJSON string to convert to a FeatureCollection.
 * @returns The GeoJSON is converted to a FeatureCollection.
 */
export function ConvertGeoJsonToFeatureCollection(geojsonString: string) {
  const format = new GeoJSON();

  return format.readFeatures(geojsonString);
}

/**
 * `Buffer` takes a GeoJSON feature and returns a GeoJSON feature with a buffer around it
 * @param feature - The GeoJSON feature to be buffered.
 * @param {BufferOptions} [options] - {
 * @returns A GeoJSON feature
 */
export function Buffer(feature, options?: BufferOptions) {
  let units = options?.units == null ? 'meters' : options.units;
  let amount = options?.amount == null ? 10 : options.amount;
  let projection =
    options?.projection == null ? 'EPSG:28355' : options.projection;

  const format = new GeoJSON();

  let convertedFeature = format.writeFeatureObject(feature, {
    featureProjection: projection,
  });

  let buffered = turf.buffer(convertedFeature, amount, { units: units });

  return format.readFeature(buffered, { featureProjection: projection });
}

/**
 * It takes two features and returns the difference between them.
 * @param featureOne - The first feature to be used in the difference.
 * @param featureTwo - The feature to subtract from featureOne.
 * @returns The difference of the two polygons.
 */
export function Difference(
  featureOne: Feature<any>,
  featureTwo: Feature<any>,
  options?
) {
  let projection =
    options?.projection == null ? 'EPSG:28355' : options.projection;

  const format = new GeoJSON();

  let featureOneGeoJson = JSON.parse(format.writeFeature(featureOne));
  let featureTwoGeoJson = JSON.parse(format.writeFeature(featureTwo));

  let featureOneTurf = GetTurf(featureOneGeoJson);
  let featureTwoTurf = GetTurf(featureTwoGeoJson);

  var difference = turf.difference(featureOneTurf, featureTwoTurf);

  return format.readFeature(difference);
}

/**
 * It takes two features, converts them to GeoJSON, then uses JTS to intersect them, then converts the
 * result back to a feature.
 * @param featureOne - The first feature to intersect
 * @param featureTwo - The feature that you want to intersect with featureOne.
 * @param [options] - {
 * @returns A GeoJSON Feature
 */
export async function Intersect(
  featureOne: any,
  featureTwo: any,
  options?: { projection?: string }
): Promise<any | null> {
  const projection = options?.projection ?? 'EPSG:28355';
  const format = new GeoJSON();

  const g1 = featureOne?.getGeometry?.();
  const g2 = featureTwo?.getGeometry?.();
  if (!g1 || !g2) return null;


  const geometryFactory = new GeometryFactory();

  const g1json = format.writeGeometryObject(g1, { dataProjection: projection, featureProjection: projection });
  const g2json = format.writeGeometryObject(g2, { dataProjection: projection, featureProjection: projection });

  const reader = new GeoJSONReader(geometryFactory);
  const writer = new GeoJSONWriter();

  let j1: any;
  let j2: any;
  try {
    j1 = reader.read(JSON.stringify(g1json));
    j2 = reader.read(JSON.stringify(g2json));
  } catch {
    return null;
  }

  try {
    if (!RelateOp.intersects(j1, j2)) return null;
  } catch {
    return null;
  }

  let inter: any;
  try {
    inter = OverlayOp.intersection(j1, j2);
  } catch {
    return null;
  }

  try {
    if (!inter || inter.isEmpty?.() || inter.getNumPoints?.() === 0) return null;
    const gj = writer.write(inter);
    const olGeom = format.readGeometry(gj as any, { dataProjection: projection, featureProjection: projection });
    return new Feature({ geometry: olGeom });
  } catch {
    return null;
  }
}

export function Intersects(featureOne, featureTwo)
{
  const format = new GeoJSON();

  let featureOneGeoJson = JSON.parse(format.writeFeature(featureOne));
  let featureTwoGeoJson = JSON.parse(format.writeFeature(featureTwo));
  const geometryFactory = new GeometryFactory();

  let reader = new GeoJSONReader(geometryFactory);
  let writer = new GeoJSONWriter();

  let featureOneGeom = reader.read(JSON.stringify(featureOneGeoJson.geometry));

  let featureTwoGeom = reader.read(JSON.stringify(featureTwoGeoJson.geometry));

  return  RelateOp.intersects(featureOneGeom, featureTwoGeom);

}

/**
 * It takes a feature, clones it, and then sets the properties of the new feature to the properties of
 * the original feature.
 * @param originalFeature - The feature that is being edited.
 * @param newFeature - The new feature that was created by the user.
 * @returns A new feature with the same properties as the original feature, but with the geometry of
 * the new feature.
 */
export function CloneProperties(
  originalFeature: Feature<any>,
  newFeature: Feature<any>
): Feature<any> {
  if (originalFeature == null || newFeature == null) {
    return null;
  }
  let properties = originalFeature.getProperties();

  Object.keys(properties).forEach((key) => {
    if (key != 'geometry') {

      if(key == 'SHAPE.LEN' || key.toLowerCase() == 'shape_length')
      {
        try{
          let length = CalculateLength(newFeature);

          newFeature.set(key, length);
        }
        catch(error)
        {
          throw error;
        }

      }
       if (key == 'SHAPE.AREA' || key.toLowerCase() == 'shape_area')
      {
        try {
          let area = CalculateArea(newFeature);
          newFeature.set(key,area);
        }
        catch(error)
        {
          throw error
        }


      }
      else
      {
        newFeature.set(key, properties[key]);
      }
    }
  });
  return newFeature;
}

/**
 * It takes a feature, converts it to GeoJSON, then calculates the length of the feature
 * @param feature - Feature<any> - The feature to calculate the length of.
 * @param [options] - { projection: string }
 * @returns The length of the line in meters.
 */
export function CalculateLength(
  feature: Feature<any>,
  options?: { projection: string }
) {
  let projection =
    options?.projection == null ? 'EPSG:28355' : options.projection;

  const format = new GeoJSON();

  let featureGeoJson = JSON.parse(
    format.writeFeature(feature, { featureProjection: projection })
  );


  let length = turf.length(featureGeoJson, {});

  return parseFloat((length * 1000).toFixed(1));
}

/**
 * It takes a feature, converts it to GeoJSON, then calculates the area of the feature in hectares.
 * @param feature - Feature<any> - The feature to calculate the area of.
 * @param [options] - { projection: string }
 * @returns The area of the polygon in hectares.
 */
export function CalculateArea(
  feature: Feature<any>,
  options?: { projection: string }
) {
  if (feature == null) {
    throw new Error('Cannot calculate area of null or undefined feature');
  }

  let projection =
    options?.projection == null ? 'EPSG:28355' : options.projection;

  let geometry = feature.getGeometry();

  // Check if geometry is not null or undefined
  if (geometry == null) {
    throw new Error('Invalid feature geometry: geometry is null or undefined');
  }

  // Check if geometry is of Polygon or MultiPolygon type
  if (!(geometry instanceof Polygon) && !(geometry instanceof MultiPolygon)) {
    throw new Error(
      'Invalid feature geometry: geometry must be of type Polygon or MultiPolygon'
    );
  }

  let coordinates = geometry.getCoordinates();

  // Check if coordinates are not null or undefined
  if (coordinates == null) {
    throw new Error('Invalid feature geometry: coordinates are null or undefined');
  }

  const format = new GeoJSON();

  let featureGeoJson = JSON.parse(
    format.writeFeature(feature, { featureProjection: projection })
  );

  let area = turf.area(featureGeoJson);

  return parseFloat((area / 10000).toFixed(3));
}
async function Intersect_ArcGIS(
  feature: Feature<LineString>,
  intersectFeature: Feature<any>
) {
  let inputGeometry = feature.getGeometry();
  let intersectGEOM = intersectFeature.getGeometry();

  let esrijsonFormat = new EsriJSON();

  let esrijsonGeometryString = esrijsonFormat.writeGeometry(inputGeometry);
  let esrijsonIntersectGeometryString =
    esrijsonFormat.writeGeometry(intersectGEOM);

  let intersectUrl = ` https://services.thelist.tas.gov.au/arcgis/rest/services/Utilities/Geometry/GeometryServer/intersect`;

  // Build formData object.
  let formData = new FormData();
  formData.append(
    'geometries',
    `{geometryType:"esriGeometryPolyline","geometries":[${esrijsonGeometryString}]}`
  );
  formData.append(
    'geometry',
    `{geometryType:"esriGeometryPolygon","geometry":${esrijsonIntersectGeometryString}}`
  );
  formData.append('sr', '28355');
  formData.append('f', 'pjson');

  let reseponse = await fetch(intersectUrl, {
    body: formData,
    method: 'post',
  });

  let featureResponse = await reseponse.json();

  if (featureResponse.geometries[0].paths.length > 0) {
    let intersectedGeometry = esrijsonFormat.readGeometry(
      featureResponse.geometries[0]
    );

    let intersectedFeature = new Feature();

    intersectedFeature.setGeometry(intersectedGeometry);

    return intersectedFeature;
  } else {
    return null;
  }
}

/**
 * Given a feature, return a turf feature of the same type
 * @param feature - The feature to be converted to a turf feature.
 * @returns A turf feature.
 */
function GetTurf(feature: any): turf.helpers.Feature<any> {
  let featureType = feature.geometry.type;
  let coordinates = feature.geometry.coordinates;

  if(coordinates.length == 0)
  {
    return null;
  }

  switch (featureType) {
    case 'Polygon':
      return turf.polygon(coordinates);
    case 'MultiPolygon':
      return turf.multiPolygon(coordinates);
    case 'LineString':
      return turf.lineString(coordinates);
    case 'MultiLineString':
      return turf.multiLineString(coordinates);
    case 'Point':
      return turf.point(coordinates);
    default:
      return undefined;
  }
}
export function createSegmentPolygons(center: Coordinate, radius: number, numSegments: number) {
  const features = [] as Feature<Polygon>[];
  const degreesPerSegment = 360 / numSegments;
  const radiansPerSegment = (Math.PI / 180) * degreesPerSegment;

  for (let i = 0; i < numSegments; i++) {
    const segmentStartAngle = radiansPerSegment * i;
    const segmentEndAngle = segmentStartAngle + radiansPerSegment;

    const segmentPoints = [];
    segmentPoints.push(center);

    const steps = 20;
    for (let step = 0; step <= steps; step++) {
      const theta =
        segmentStartAngle +
        (segmentEndAngle - segmentStartAngle) * (step / steps);
      const x = center[0] + radius * Math.cos(theta);
      const y = center[1] + radius * Math.sin(theta);
      segmentPoints.push([x, y]);
    }

    segmentPoints.push(center);

    const polygon = new Polygon([segmentPoints]);
    const feature = new Feature(polygon);
    features.push(feature);
  }

  return features;
}
